/**
 * Finance module data shapes — overhead expenses, payroll positions, and the
 * cached QuickBooks balance sheet summary. Admin-only collections; see the
 * matching blocks in firestore.rules.
 */

import type { PurchaseOverheadGroup } from './purchasing';

export type OverheadFrequency =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'one-time';

export type OverheadCategory =
  | 'rent'
  | 'software'
  | 'utilities'
  | 'insurance'
  | 'marketing'
  | 'services'
  | 'loans'
  | 'other';

export const OVERHEAD_CATEGORIES: OverheadCategory[] = [
  'rent',
  'software',
  'utilities',
  'insurance',
  'marketing',
  'services',
  'loans',
  'other',
];

export const OVERHEAD_CATEGORY_LABELS: Record<OverheadCategory, string> = {
  rent: 'Rent & Facilities',
  software: 'Software & SaaS',
  utilities: 'Utilities',
  insurance: 'Insurance',
  marketing: 'Marketing',
  services: 'Professional Services',
  loans: 'Loans & Debt Service',
  other: 'Other',
};

export const OVERHEAD_FREQUENCIES: OverheadFrequency[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'annually',
  'one-time',
];

export const OVERHEAD_FREQUENCY_LABELS: Record<OverheadFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  'one-time': 'One-time',
};

/**
 * How an overhead row's amount scales over time.
 *  - 'fixed': the amount is a flat company-wide cost at the given frequency.
 *  - 'per_active_employee': the amount is the cost PER employee at the given
 *    frequency, multiplied by the count of active payroll positions for the
 *    target month (uniforms, PPE, cell stipends, etc).
 */
export type OverheadScalesWith = 'fixed' | 'per_active_employee';

export const OVERHEAD_SCALES_WITH_LABELS: Record<OverheadScalesWith, string> = {
  fixed: 'Fixed monthly cost',
  per_active_employee: 'Per active employee',
};

export function clampOverheadRecurringDay(day: unknown): number | null {
  const n = typeof day === 'number' ? day : Number(day);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < 1 || rounded > 31) return null;
  return rounded;
}

export function clampOverheadRenewalMonth(month: unknown): number | null {
  const n = typeof month === 'number' ? month : Number(month);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < 1 || rounded > 12) return null;
  return rounded;
}

export function resolveOverheadRecurringDay(
  expense: Pick<OverheadExpense, 'recurringDay' | 'startDate'>,
): number {
  const explicit = clampOverheadRecurringDay(expense.recurringDay);
  if (explicit != null) return explicit;
  const start = expense.startDate ? new Date(expense.startDate) : null;
  if (start && !Number.isNaN(start.getTime())) return start.getDate();
  return 1;
}

/** Calendar month (1–12) an annual charge renews. Falls back to startDate. */
export function resolveOverheadRenewalMonth(
  expense: Pick<OverheadExpense, 'renewalMonth' | 'startDate'>,
): number {
  const explicit = clampOverheadRenewalMonth(expense.renewalMonth);
  if (explicit != null) return explicit;
  const start = expense.startDate ? new Date(expense.startDate) : null;
  if (start && !Number.isNaN(start.getTime())) return start.getMonth() + 1;
  return 1;
}

export const OVERHEAD_MONTH_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 12 },
  (_, i) => ({
    value: i + 1,
    label: new Date(2020, i, 1).toLocaleDateString('en-US', { month: 'long' }),
  }),
);

export function formatOverheadMonth(month: number, style: 'long' | 'short' = 'long'): string {
  const n = clampOverheadRenewalMonth(month) ?? 1;
  return new Date(2020, n - 1, 1).toLocaleDateString('en-US', { month: style });
}

/** Recurring day, plus renewal month for annual rows (e.g. "Feb 2nd"). */
export function formatOverheadBillOn(
  expense: Pick<OverheadExpense, 'frequency' | 'recurringDay' | 'renewalMonth' | 'startDate'>,
): string {
  const day = formatOrdinalDay(resolveOverheadRecurringDay(expense));
  if (expense.frequency === 'annually') {
    return `${formatOverheadMonth(resolveOverheadRenewalMonth(expense), 'short')} ${day}`;
  }
  return day;
}

export function formatOrdinalDay(day: number): string {
  const n = clampOverheadRecurringDay(day) ?? 1;
  const mod100 = n % 100;
  const suffix =
    mod100 >= 11 && mod100 <= 13
      ? 'th'
      : n % 10 === 1
        ? 'st'
        : n % 10 === 2
          ? 'nd'
          : n % 10 === 3
            ? 'rd'
            : 'th';
  return `${n}${suffix}`;
}

export interface OverheadExpense {
  id: string;
  name: string;
  category: OverheadCategory;
  amount: number;
  frequency: OverheadFrequency;
  /** Defaults to 'fixed' when missing. */
  scalesWith: OverheadScalesWith;
  startDate: Date;
  /** Day of month this bills (1–31). Falls back to `startDate`'s day when unset. */
  recurringDay?: number | null;
  /** Calendar month (1–12) an annual charge renews. Falls back to `startDate`. */
  renewalMonth?: number | null;
  endDate?: Date | null;
  vendor?: string;
  notes?: string;
  isActive: boolean;
  /** Optional assignment to a purchasing OpEx bucket for grouping / PO caps. */
  opexGroup?: PurchaseOverheadGroup | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export type PayrollDepartment =
  | 'shop'
  | 'office'
  | 'sales'
  | 'engineering'
  | 'leadership'
  | 'other';

export const PAYROLL_DEPARTMENTS: PayrollDepartment[] = [
  'shop',
  'office',
  'sales',
  'engineering',
  'leadership',
  'other',
];

export const PAYROLL_DEPARTMENT_LABELS: Record<PayrollDepartment, string> = {
  shop: 'Shop',
  office: 'Office',
  sales: 'Sales',
  engineering: 'Engineering',
  leadership: 'Leadership',
  other: 'Other',
};

export type PayrollStatus = 'filled' | 'open' | 'planned';

export const PAYROLL_STATUSES: PayrollStatus[] = ['filled', 'open', 'planned'];

export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = {
  filled: 'Filled',
  open: 'Open',
  planned: 'Planned',
};

/**
 * @deprecated Replaced by the itemized payroll model in
 * {@link PayrollPosition}. The reader uses this constant only to migrate
 * any legacy docs that still carry `benefitsAndTaxRate`.
 */
export const DEFAULT_PAYROLL_BURDEN_RATE = 0.2;

export const DEFAULT_PTO_DAYS_PER_YEAR = 10;
export const DEFAULT_SEP_IRA_RATE = 0.03;
/** FICA (6.2%) + Medicare (1.45%). FUTA/SUTA layered manually if needed. */
export const DEFAULT_EMPLOYER_TAX_RATE = 0.0765;
/** Standard accounting convention for converting annual salary into a per-day rate. */
export const WORK_DAYS_PER_YEAR = 260;
/** Full-time default for hourly → annual conversion. */
export const DEFAULT_HOURS_PER_WEEK = 40;
export const WEEKS_PER_YEAR = 52;
/** 40 × 52 — the canonical "2,080 hours / yr" benchmark. */
export const DEFAULT_HOURS_PER_YEAR = DEFAULT_HOURS_PER_WEEK * WEEKS_PER_YEAR;

/**
 * How a position's annual salary is entered. `annualSalary` remains the
 * canonical value the forecast consumes; for hourly roles it is derived from
 * `hourlyRate × hoursPerWeek × 52`.
 */
export type PayType = 'salary' | 'hourly';

export const PAY_TYPE_LABELS: Record<PayType, string> = {
  salary: 'Salary',
  hourly: 'Hourly',
};

export type CompChangeKind = 'percent' | 'dollar' | 'set';

export type CompPeriodSource = 'hire' | 'manual' | 'cola' | 'raise';

/** Future raise scheduled to take effect on a given date. */
export interface PayrollScheduledRaise {
  id: string;
  effectiveDate: Date;
  kind: CompChangeKind;
  /** percent (3 = 3%), dollar ($5k), or set (new annual salary). */
  amount: number;
  label?: string;
  createdAt: Date;
}

/** Historical or current pay period for comp history. */
export interface PayrollCompPeriod {
  id: string;
  effectiveDate: Date;
  endDate?: Date | null;
  annualSalary: number;
  hourlyRate?: number | null;
  hoursPerWeek?: number | null;
  label?: string;
  source: CompPeriodSource;
}

export interface PayrollPosition {
  id: string;
  title: string;
  department: PayrollDepartment;
  status: PayrollStatus;
  /**
   * Annualized base pay. Forecast is always driven by this value.
   * - When payType is 'salary', this is entered directly.
   * - When payType is 'hourly', this is computed from
   *   `hourlyRate × hoursPerWeek × 52` and re-computed on every save.
   */
  annualSalary: number;
  /** How base pay is entered. Defaults to 'salary' for back-compat. */
  payType: PayType;
  /** Hourly rate when payType === 'hourly'. */
  hourlyRate?: number | null;
  /** Hours / week when payType === 'hourly'. Defaults to 40. */
  hoursPerWeek?: number | null;
  /** Monthly health-reimbursement payment to the employee. Taxable income. */
  healthReimbursementMonthly: number;
  /** Paid time off allowance per year (default 10 = 5 vac + 2 sick + 2 bereavement + 1 personal). */
  ptoDaysPerYear: number;
  /** Per-position misc cost ($/mo) — car allowance, role-specific cell plan, etc. */
  monthlyEmployeeExtras: number;
  /** Optional short label describing what `monthlyEmployeeExtras` covers. */
  monthlyEmployeeExtrasNote?: string;
  /** Overrides the company-default SEP IRA % when set. */
  sepIraRateOverride?: number | null;
  /** Overrides the company-default employer-tax % when set. */
  employerTaxRateOverride?: number | null;
  /**
   * @deprecated Legacy flat burden % migrated into `employerTaxRateOverride`
   * on read so loaded cost stays visually constant until the admin re-edits.
   */
  benefitsAndTaxRate?: number | null;
  /** Hire date or planned start date. */
  startDate: Date;
  endDate?: Date | null;
  /** Optional CRM user uid when status === 'filled'. */
  crmUserId?: string | null;
  /** Two-letter work state for employer SUTA (overrides linked-user location when set). */
  workState?: string | null;
  notes?: string;
  isActive: boolean;
  /** Future scheduled raises (auto-applied when effective date arrives). */
  scheduledRaises?: PayrollScheduledRaise[];
  /** Comp history — closed and current open periods. */
  compPeriods?: PayrollCompPeriod[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

/**
 * Company-wide payroll defaults. Stored at `financeSettings/payrollSettings`.
 * Per-position overrides win when set, otherwise these rates are used in the
 * forecast.
 */
export interface PayrollSettings {
  sepIraRate: number;
  employerTaxRate: number;
  defaultPtoDaysPerYear: number;
  defaultHealthReimbursementMonthly: number;
  defaultMonthlyEmployeeExtras: number;
  /** Default work state when a position has no workState and no linked user location. */
  defaultWorkState?: string;
  updatedAt?: Date | null;
  updatedBy?: string;
}

export const DEFAULT_PAYROLL_SETTINGS: PayrollSettings = {
  sepIraRate: DEFAULT_SEP_IRA_RATE,
  employerTaxRate: DEFAULT_EMPLOYER_TAX_RATE,
  defaultPtoDaysPerYear: DEFAULT_PTO_DAYS_PER_YEAR,
  defaultHealthReimbursementMonthly: 0,
  defaultMonthlyEmployeeExtras: 0,
  defaultWorkState: 'VA',
  updatedAt: null,
};

export interface BalanceSheetCache {
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  /** Date QuickBooks reported the balances as of (yyyy-mm-dd). */
  asOfDate: string | null;
  /** When this cache row was written. */
  lastSyncedAt: Date | null;
}

// ─── Historical cashflow (QuickBooks P&L by Month) ───

/** Which accounting method the historical P&L was pulled under. */
export type CashflowMethod = 'cash' | 'accrual';

/**
 * P&L section that a leaf account belongs to. We classify by walking up from
 * each leaf to its enclosing section's `group` key in the QB response.
 */
export type CashflowAccountType =
  | 'Income'
  | 'COGS'
  | 'Expense'
  | 'OtherIncome'
  | 'OtherExpense'
  | 'Other';

export interface CashflowAccountLine {
  /** QuickBooks account display name, e.g. "Software Subscriptions". */
  name: string;
  accountType: CashflowAccountType;
  /**
   * When true, `monthly` holds a P&L section subtotal (e.g. "Ad Spend",
   * "Bank & Merchant Fees") rather than a single leaf account.
   */
  isCategoryTotal?: boolean;
  /** Parent expense section in the QB P&L tree, e.g. "Ad Spend" for leaf lines beneath it. */
  parentExpenseSection?: string;
  /**
   * Per-month value for this account.
   * Keyed by ISO `YYYY-MM` (the first day of the month QB reported on).
   * Months with no value for this account are simply absent.
   */
  monthly: Record<string, number>;
}

/** Roll-ups per month for one accounting method. */
export interface CashflowMonthlyTotals {
  income: number;
  cogs: number;
  expense: number;
  /** income - cogs - expense + other - otherExpense. Stored so the UI doesn't recompute. */
  netIncome: number;
}

export interface CashflowMethodData {
  /** Months covered in chronological order, `YYYY-MM`. */
  months: string[];
  /** Totals per month keyed by `YYYY-MM`. */
  totals: Record<string, CashflowMonthlyTotals>;
  /** All leaf accounts that appeared in the report, with per-month values. */
  accounts: CashflowAccountLine[];
}

/**
 * Firestore doc `financeSettings/cashflowHistoryCache`. Holds both accounting
 * methods so the UI toggle does not require another API call.
 */
export interface CashflowHistoryCache {
  cash: CashflowMethodData | null;
  accrual: CashflowMethodData | null;
  /** QB date range used for the report, YYYY-MM-DD. */
  rangeStart: string | null;
  rangeEnd: string | null;
  /** When this cache row was written. */
  lastSyncedAt: Date | null;
}

/** One day's Sequence pod balance snapshot (financeSettings/dailyFinanceReport). */
export interface DailyFinanceReportDay {
  sequencePodAmount: number;
  pods?: Array<{ id: string; name: string; balance: number }>;
}

/**
 * Daily finance report — Sequence pod cash total for the Finances chart.
 * `history` is keyed by YYYY-MM-DD (America/Chicago).
 */
export interface DailyFinanceReport {
  /** Latest Sequence cash (pods, Payments, and Chase Short Term Ops). */
  sequencePodAmount: number;
  /** Date of the latest snapshot, YYYY-MM-DD. */
  dateKey: string | null;
  sequencePods?: Array<{ id: string; name: string; balance: number }>;
  history?: Record<string, DailyFinanceReportDay>;
  source?: 'sequence';
  updatedAt: Date | null;
}

// ─── Planned cash obligations (bonuses + entity tax) ───

export type EntityTaxType = 'llc_s_corp';

export type PlannedKind = 'bonus' | 'tax' | 'other';

export type BonusPlanMode = 'percent_of_profit' | 'percent_of_salary' | 'fixed' | 'profit_pool';

export type BonusPayoutSplit = 'lump' | 'custom';

export const BONUS_PLAN_MODES: BonusPlanMode[] = [
  'percent_of_profit',
  'percent_of_salary',
  'fixed',
  'profit_pool',
];

export const BONUS_PLAN_MODE_LABELS: Record<BonusPlanMode, string> = {
  percent_of_profit: '% of profit per person',
  percent_of_salary: '% of salary',
  fixed: 'Fixed $ per person',
  profit_pool: 'Fixed dollar pool',
};

export interface BonusPositionOverride {
  positionId: string;
  included: boolean;
  /** Override default % (3 = 3%). Ignored when `amount` is set. */
  percent?: number | null;
  /** Override to a fixed dollar bonus for this person. */
  amount?: number | null;
}

export interface BonusPayoutSlice {
  id: string;
  date: Date;
  /** Share of the total cash-out (100 = 100%). */
  percent: number;
  label?: string;
}

export interface BonusPlan {
  year: number;
  enabled: boolean;
  mode: BonusPlanMode;
  /**
   * Default % when a person has no override: % of year-end profit
   * (`percent_of_profit`) or % of that person's salary (`percent_of_salary`).
   */
  defaultPercent: number;
  /** Fixed $ per eligible person when mode is `fixed`. */
  defaultFixedAmount: number;
  /** Company pool $ when mode is `profit_pool`. */
  poolAmount: number;
  includeEmployerTax: boolean;
  payoutSplit: BonusPayoutSplit;
  /** 1–12, used for the lump payout. */
  payoutMonth: number;
  /** 1–31, used for the lump payout. */
  payoutDay: number;
  customSlices: BonusPayoutSlice[];
  overrides: BonusPositionOverride[];
  updatedAt?: Date | null;
  updatedBy?: string;
}

export interface TaxPlan {
  year: number;
  enabled: boolean;
  /**
   * Estimated year-end *net* profit (already after regular W-2 / opex / COGS).
   * K-1 remainder = max(0, profit − year-end bonus cash-out).
   */
  estimatedAnnualProfit: number;
  /** Book net worth used for TN franchise tax. */
  estimatedNetWorth: number;
  /** Owner federal effective rate (0.32 = 32%). */
  ownerFederalEffectiveRate: number;
  qbiEnabled: boolean;
  /** 0.20 = 20% QBI. */
  qbiRate: number;
  /** When true, federal 1040-ES estimates are company cash-out (owner distributions). */
  includeOwnerTaxDistributions: boolean;
  tnExciseRate: number;
  tnFranchisePerHundred: number;
  tnFranchiseMin: number;
  updatedAt?: Date | null;
  updatedBy?: string;
}

export interface ObligationBonusDefaults {
  defaultPercent: number;
  payoutMonth: number;
  payoutDay: number;
  includeEmployerTax: boolean;
}

export interface ObligationTaxDefaults {
  ownerFederalEffectiveRate: number;
  qbiEnabled: boolean;
  includeOwnerTaxDistributions: boolean;
}

/** Company defaults. Stored at `financeSettings/obligationSettings`. */
export interface ObligationSettings {
  entityType: EntityTaxType;
  state: string;
  /** 1–12. Calendar-year default is December. */
  fiscalYearEndMonth: number;
  bonusDefaults: ObligationBonusDefaults;
  taxDefaults: ObligationTaxDefaults;
  updatedAt?: Date | null;
  updatedBy?: string;
}

export const DEFAULT_TN_EXCISE_RATE = 0.065;
export const DEFAULT_TN_FRANCHISE_PER_HUNDRED = 0.25;
export const DEFAULT_TN_FRANCHISE_MIN = 100;
export const DEFAULT_QBI_RATE = 0.2;
export const DEFAULT_OWNER_FEDERAL_EFFECTIVE_RATE = 0.32;
export const DEFAULT_BONUS_PERCENT = 10;

export const DEFAULT_OBLIGATION_SETTINGS: ObligationSettings = {
  entityType: 'llc_s_corp',
  state: 'TN',
  fiscalYearEndMonth: 12,
  bonusDefaults: {
    defaultPercent: DEFAULT_BONUS_PERCENT,
    payoutMonth: 12,
    payoutDay: 15,
    includeEmployerTax: true,
  },
  taxDefaults: {
    ownerFederalEffectiveRate: DEFAULT_OWNER_FEDERAL_EFFECTIVE_RATE,
    qbiEnabled: true,
    includeOwnerTaxDistributions: true,
  },
  updatedAt: null,
};

export function createDefaultBonusPlan(
  year: number,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): BonusPlan {
  const d = settings.bonusDefaults;
  return {
    year,
    enabled: true,
    mode: 'percent_of_profit',
    defaultPercent: 0,
    defaultFixedAmount: 0,
    poolAmount: 0,
    includeEmployerTax: d.includeEmployerTax,
    payoutSplit: 'lump',
    payoutMonth: d.payoutMonth,
    payoutDay: d.payoutDay,
    customSlices: [],
    overrides: [],
    updatedAt: null,
  };
}

export function createDefaultTaxPlan(
  year: number,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): TaxPlan {
  const d = settings.taxDefaults;
  return {
    year,
    enabled: true,
    estimatedAnnualProfit: 0,
    estimatedNetWorth: 0,
    ownerFederalEffectiveRate: d.ownerFederalEffectiveRate,
    qbiEnabled: d.qbiEnabled,
    qbiRate: DEFAULT_QBI_RATE,
    includeOwnerTaxDistributions: d.includeOwnerTaxDistributions,
    tnExciseRate: DEFAULT_TN_EXCISE_RATE,
    tnFranchisePerHundred: DEFAULT_TN_FRANCHISE_PER_HUNDRED,
    tnFranchiseMin: DEFAULT_TN_FRANCHISE_MIN,
    updatedAt: null,
  };
}

/** One dated company cash-out used by the forecast projection. */
export interface ObligationSlice {
  plannedKind: PlannedKind;
  label: string;
  projectLabel: string;
  amount: number;
  dueDate: Date;
  estimated: boolean;
}

// ─── CFO cockpit ───

export type OwnerDrawKind = 'tax_cover' | 'profit' | 'other';

export const OWNER_DRAW_KINDS: OwnerDrawKind[] = ['tax_cover', 'profit', 'other'];

export const OWNER_DRAW_KIND_LABELS: Record<OwnerDrawKind, string> = {
  tax_cover: 'Cover owner tax',
  profit: 'Profit take',
  other: 'Other',
};

/** Company defaults for the Forecast Cockpit. Stored at `financeSettings/cockpitSettings`. */
export interface CockpitSettings {
  /** Never-below cash floor used against the projected balance. */
  minimumCash: number;
  /** Free-text memo for the reasonable-compensation snapshot (CPA packet). */
  compensationNotes: string;
  /**
   * Payroll position IDs that have an ownership stake.
   * Null means “not set yet” — Compensation uses the leadership-title guess.
   */
  ownerPositionIds: string[] | null;
  updatedAt?: Date | null;
  updatedBy?: string;
}

export const DEFAULT_COCKPIT_SETTINGS: CockpitSettings = {
  minimumCash: 0,
  compensationNotes: '',
  ownerPositionIds: null,
  updatedAt: null,
};

export interface OwnerDistribution {
  id: string;
  date: Date;
  amount: number;
  kind: OwnerDrawKind;
  note: string;
  createdAt: Date;
  createdBy: string;
}

export interface DebtInstrument {
  id: string;
  name: string;
  lender: string;
  principalRemaining: number;
  /** Annual interest rate, percent (6.5 = 6.5%). */
  annualRatePct: number;
  paymentAmount: number;
  /** 1–28 preferred so every month has the day. */
  paymentDay: number;
  /** Null = open-ended. */
  remainingPayments: number | null;
  balloonAmount: number;
  balloonDate: Date | null;
  notes: string;
  isActive: boolean;
  /** True when the payment is already in overhead burn (avoid double-count). */
  alreadyInBurn: boolean;
  createdAt: Date;
  updatedAt: Date;
}
